'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useReportWebVitals } from 'next/web-vitals';
import NextTopLoader from 'nextjs-toploader';

import { LocaleProvider } from '@/__generated__/i18n.next';
import { Provider } from '@/components/__generated__/chakra-ui/provider';
import { Toaster } from '@/components/__generated__/chakra-ui/toaster';
import { ChatPaneProvider } from '@/core/components/ChatPaneContext';

import { system } from '../../theme';
import { BrowserCompatibilityChecker } from './BrowserCompatibilityChecker';

const queryClient = new QueryClient();

export const Providers: React.FC<{ children: React.ReactNode }> = (props) => {
  // cf. https://nextjs.org/docs/app/api-reference/functions/use-report-web-vitals#sending-results-to-external-systems
  useReportWebVitals((metric: { id: string; label: string; name: string; value: number }) => {
    // eslint-disable-next-line
    (globalThis as any)?.gtag?.('event', metric.name, {
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value), // values must be integers
      event_label: metric.id, // id unique to current page load
      non_interaction: true, // avoids affecting bounce rate.
    });
  });

  return (
    <>
      <QueryClientProvider client={queryClient}>
        {/* Locale Context */}
        <LocaleProvider>
          {/* Chakra UI */}
          <Provider enableSystem={false} system={system}>
            <BrowserCompatibilityChecker>
              <PageTransitionProgressBar />
              <ChatPaneProvider>{props.children}</ChatPaneProvider>
            </BrowserCompatibilityChecker>
            <Toaster />
          </Provider>
        </LocaleProvider>
      </QueryClientProvider>
    </>
  );
};

const PageTransitionProgressBar: React.FC = () => {
  // Use a hardcoded brand color since theme structure changed in v3
  return <NextTopLoader color="#38A169" shadow={false} showSpinner={false} />; // Chakra green.500 equivalent
};
